import React, { useEffect, useState, useCallback } from "react";
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  RefreshControl,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useDispatch } from "react-redux";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  NotificationItem,
} from "../../../lib/notifications";
import { notificationActions } from "../../../store/slices/notificationSlice";
import i18n from "../../../locales/i18n";
import CustomAlert from "../../../components/common/CustomAlert";

const PAGE_SIZE = 20;

const typeIcons: Record<string, keyof typeof Ionicons.glyphMap> = {
  general: "notifications-outline",
  alert: "warning-outline",
  promotion: "pricetag-outline",
  update: "refresh-outline",
  order: "cart-outline",
  system: "cog-outline",
};

const NotificationsScreen = () => {
  const dispatch = useDispatch();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState<{
    visible: boolean;
    id: number | null;
  }>({ visible: false, id: null });

  const fetchNotifications = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        const data = await getNotifications(pageNum, PAGE_SIZE);
        setNotifications((prev) =>
          append ? [...prev, ...data.notifications] : data.notifications,
        );
        setTotal(data.total);
        dispatch(notificationActions.setUnreadCount(data.unreadCount));
      } catch {}
    },
    [dispatch],
  );

  useEffect(() => {
    fetchNotifications(1).finally(() => setLoading(false));
  }, [fetchNotifications]);

  const onRefresh = async () => {
    setRefreshing(true);
    setPage(1);
    await fetchNotifications(1);
    setRefreshing(false);
  };

  const loadMore = async () => {
    if (loadingMore || notifications.length >= total) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchNotifications(nextPage, true);
    setLoadingMore(false);
  };

  const handleRead = async (item: NotificationItem) => {
    if (!item.isRead) {
      const res = await markNotificationAsRead(item.id_notification);
      if (res.success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id_notification === item.id_notification
              ? { ...n, isRead: true }
              : n,
          ),
        );
        dispatch(notificationActions.decrementUnreadCount());
      }
    }
  };

  const handleMarkAllRead = async () => {
    const res = await markAllNotificationsAsRead();
    if (res.success) {
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      dispatch(notificationActions.resetUnreadCount());
    }
  };

  const handleDelete = (id: number) => {
    const doDelete = async () => {
      const res = await deleteNotification(id);
      if (res.success) {
        const deleted = notifications.find((n) => n.id_notification === id);
        setNotifications((prev) =>
          prev.filter((n) => n.id_notification !== id),
        );
        setTotal((prev) => prev - 1);
        if (deleted && !deleted.isRead) {
          dispatch(notificationActions.decrementUnreadCount());
        }
      }
    };

    if (Platform.OS === "web") {
      doDelete();
    } else {
      setDeleteAlert({ visible: true, id });
    }
  };

  const executeDelete = async () => {
    if (deleteAlert.id !== null) {
      const res = await deleteNotification(deleteAlert.id);
      if (res.success) {
        const deleted = notifications.find(
          (n) => n.id_notification === deleteAlert.id,
        );
        setNotifications((prev) =>
          prev.filter((n) => n.id_notification !== deleteAlert.id),
        );
        setTotal((prev) => prev - 1);
        if (deleted && !deleted.isRead) {
          dispatch(notificationActions.decrementUnreadCount());
        }
      }
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return i18n.t("justNow") || "Just now";
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString();
  };

  /** Get the localized title/message from translations if available */
  const getLocalizedContent = (item: NotificationItem) => {
    const lang = i18n.locale as string;
    const translations = item.data?.translations;
    if (translations && translations[lang]) {
      return {
        title: translations[lang].title || item.title,
        message: translations[lang].message || item.message,
      };
    }
    return { title: item.title, message: item.message };
  };

  const renderItem = ({ item }: { item: NotificationItem }) => {
    const { title: localTitle, message: localMessage } =
      getLocalizedContent(item);
    return (
      <TouchableOpacity
        onPress={() => handleRead(item)}
        activeOpacity={0.7}
        style={[styles.card, !item.isRead && styles.cardUnread]}
      >
        <View style={styles.cardLeft}>
          <View
            style={[
              styles.iconCircle,
              { backgroundColor: !item.isRead ? "#DBEAFE" : "#F3F4F6" },
            ]}
          >
            <Ionicons
              name={typeIcons[item.type] || "notifications-outline"}
              size={20}
              color={!item.isRead ? "#2563EB" : "#9CA3AF"}
            />
          </View>
        </View>
        <View style={styles.cardBody}>
          <Text
            style={[styles.cardTitle, !item.isRead && styles.cardTitleUnread]}
            numberOfLines={1}
          >
            {localTitle}
          </Text>
          <Text style={styles.cardMessage} numberOfLines={2}>
            {localMessage}
          </Text>
          <Text style={styles.cardTime}>{formatTime(item.created_at)}</Text>
        </View>
        <TouchableOpacity
          onPress={() => handleDelete(item.id_notification)}
          style={styles.deleteBtn}
        >
          <Ionicons name="trash-outline" size={18} color="#EF4444" />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  const renderEmpty = () => (
    <View style={styles.empty}>
      <Ionicons name="notifications-off-outline" size={48} color="#D1D5DB" />
      <Text style={styles.emptyText}>
        {i18n.t("noNotifications") || "No notifications yet"}
      </Text>
    </View>
  );

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: "#F9FAFB" }}>
        <StandardNavigation
          title={i18n.t("notifications") || "Notifications"}
        />
        <View style={styles.loader}>
          <ActivityIndicator size="large" color="rgba(19, 121, 162, 1)" />
        </View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: "#F9FAFB" }}>
      <StandardNavigation
        title={i18n.t("notifications") || "Notifications"}
        icon={
          notifications.some((n) => !n.isRead) ? (
            <TouchableOpacity onPress={handleMarkAllRead}>
              <Ionicons name="checkmark-done" size={24} color="white" />
            </TouchableOpacity>
          ) : null
        }
      />
      <FlatList
        data={notifications}
        keyExtractor={(item) => String(item.id_notification)}
        renderItem={renderItem}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={
          notifications.length === 0 ? { flex: 1 } : { paddingVertical: 8 }
        }
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={["rgba(19, 121, 162, 1)"]}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.4}
        ListFooterComponent={
          loadingMore ? (
            <ActivityIndicator
              style={{ marginVertical: 16 }}
              color="rgba(19, 121, 162, 1)"
            />
          ) : null
        }
      />
      <CustomAlert
        visible={deleteAlert.visible}
        title={i18n.t("deleteNotification") || "Delete"}
        message={
          i18n.t("deleteNotificationConfirm") || "Delete this notification?"
        }
        icon="trash-outline"
        iconColor="#DC2626"
        onDismiss={() => setDeleteAlert({ visible: false, id: null })}
        buttons={[
          {
            text: i18n.t("cancel") || "Cancel",
            style: "cancel",
          },
          {
            text: i18n.t("delete") || "Delete",
            style: "destructive",
            onPress: executeDelete,
          },
        ]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  loader: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    marginHorizontal: 12,
    marginVertical: 4,
    borderRadius: 12,
    padding: 14,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  cardUnread: {
    borderLeftWidth: 3,
    borderLeftColor: "#2563EB",
  },
  cardLeft: {
    marginRight: 12,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  cardBody: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 14,
    color: "#374151",
    fontFamily: "kawkabBold",
    marginBottom: 2,
  },
  cardTitleUnread: {
    color: "#111827",
  },
  cardMessage: {
    fontSize: 13,
    color: "#6B7280",
    lineHeight: 18,
    marginBottom: 4,
  },
  cardTime: {
    fontSize: 11,
    color: "#9CA3AF",
  },
  deleteBtn: {
    padding: 8,
  },
  empty: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 60,
  },
  emptyText: {
    marginTop: 12,
    fontSize: 15,
    color: "#9CA3AF",
    fontFamily: "kawkabReg",
  },
});

export default NotificationsScreen;
